import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { Content } from "../components/content";
import RecentMediaLinesDocList/*, { AllDocList }*/ from "../components/doclist";
import { GlobalSearchResultList } from "../components/global-search-result-list";
import { IAuthContextProps, withAuthContext } from "../components/with-auth-context";
import { ISnackbarContextProps, withSnackbar } from "../components/with-snackbar";
import { ItemSearchModel } from "../model/autogen/ItemSearchModel";
import { Typography, Button, FormGroup, FormControlLabel, Checkbox } from "@material-ui/core";
import { UserLevel } from "../model/autogen/UserLevel";
import { RoutePaths } from "../route-paths";
import { NBSP } from "../utils/string";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser"
import AccountCircleIcon from "@material-ui/icons/AccountCircle"
import { InfiniteListLoading, InfiniteScrollerHelper, InfiniteScrollerState } from "./infinite-scroller-helper";
import { GlobalAdvancedSearchInputModel } from "../model/autogen/GlobalAdvancedSearchInputModel";
import { SearchableContentType } from "model/autogen/SearchableContentType";
import { Iif } from "../components/iif";
import { TagModel } from "../model/autogen/TagModel";
import { TagRequestFactory } from "model/autogen/TagController";

export interface IRecentContentPageProps {
    type: SearchableContentType;
    showItemTags?: boolean;
    CustomComponent?: CustomComponentRenderer;
}

type CustomComponentRenderer = (props: { items: ItemSearchModel[] }) => JSX.Element;

type InfinitScrollerHelperState = InfiniteScrollerState & GlobalAdvancedSearchInputModel;

export interface IRecentContentPageState extends InfinitScrollerHelperState {
    useCustomComponent: boolean;
    resourceTags?: TagModel[];
    selectedResourceTagIds: number[];
}

type RecentContentPageComponentProps = RouteComponentProps<any> & IAuthContextProps & ISnackbarContextProps & IRecentContentPageProps;

export class RecentContentPageComponent extends React.Component<RecentContentPageComponentProps, IRecentContentPageState> {
    private helper: InfiniteScrollerHelper;
    constructor(props: RecentContentPageComponentProps) {
        super(props);
        this.helper = new InfiniteScrollerHelper(
            () => this.state,
            (newState, callback) => this.setState(newState, callback),
            () => this.props
        );
        this.state = {
            ...this.helper.getInitialState([props.type]),
            isSearching: false,
            results: undefined,
            useCustomComponent: true,
            startAt: undefined,
            hasMore: false,
            selectedResourceTagIds: []
        };
    }
    async componentDidMount() {
        if (this.props.type == SearchableContentType.Resource) {
            const tags = await this.props.callApi(f => TagRequestFactory.list(f, true));
            this.setState({
                resourceTags: tags,
                selectedResourceTagIds: tags.map(t => t.id),
                tags: [...tags]
            });
        }
        this.requestData(0);
    }
    private requestData = (startingCount?: number) => {
        this.helper.performSearch(startingCount)
    }
    toggleShowCustomComponent = () => {
        this.requestData();
        this.setState({ useCustomComponent: !this.state.useCustomComponent })
    }
    private getMoreResults = () => {
        this.helper.fetchMoreResults();
    }
    private onTagToggle = (tagId: number, checked: boolean) => {
        let currentTagIds = this.state.selectedResourceTagIds || [];
        if (checked) {
            currentTagIds.push(tagId);
        } else {
            currentTagIds = currentTagIds.filter(tid => tid != tagId);
        }
        const tags = [];
        if (this.state.resourceTags) {
            for (const ctid of currentTagIds) {
                const mt = this.state.resourceTags.find(rt => rt.id == ctid);
                if (mt) {
                    tags.push({ ...mt });
                }
            }
        }
        this.setState({ selectedResourceTagIds: currentTagIds, tags: tags }, () => {
            this.requestData();
        });
    }
    render(): JSX.Element {
        const { user, type, CustomComponent } = this.props;
        const { results, useCustomComponent, startAt, isSearching, resourceTags, selectedResourceTagIds } = this.state;

        // todo: implement for other items
        let list: ItemSearchModel[] = [];
        switch (type) {
            case SearchableContentType.MediaLines:
                list = results ? (results.mediaLines || []) : [];
                break;
        }

        let typeName = `${type}`;
        if (type == SearchableContentType.Quote) {
            typeName = "Transcripts";
        }

        const isEditor = user.level >= UserLevel.Editor;

        // custom component is used to render a droppable doc list, for now we only use it for medialines where
        // the user has low access level
        const showCustomComponent = CustomComponent !== undefined && useCustomComponent;// && isEditor;
        return <Content>
            <div>
                <InfiniteListLoading isSearching={isSearching} startAt={startAt} results={results}>
                    <Typography variant="h5">Recent {typeName}{NBSP}{user.level >= UserLevel.External && <Link to={{ pathname: RoutePaths.AdvancedSearch, search: `?type=${type}&autoSearch=1` }}>(Search all {typeName})</Link>}</Typography>
                    <Iif show={type == SearchableContentType.Resource}>
                        {resourceTags && <FormGroup row>
                            {resourceTags.map(rt => <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedResourceTagIds.indexOf(rt.id) >= 0}
                                        onChange={(e, c) => this.onTagToggle(rt.id, c)}
                                        value={rt.id}
                                    />
                                }
                                label={rt.name}
                            />)}
                        </FormGroup>}
                    </Iif>
                    {(CustomComponent !== undefined && isEditor) &&
                        <Button variant="contained" onClick={this.toggleShowCustomComponent} color="primary" style={{ float: 'right', marginTop: -30, marginBottom: 20 }}>{useCustomComponent ? <><VerifiedUserIcon />{NBSP} Admin View</> : <><AccountCircleIcon />{NBSP} User View</>}</Button>}
                    <div style={{ clear: "both" }} />
                    {(CustomComponent !== undefined && showCustomComponent) ? <CustomComponent items={list} /> :
                        <GlobalSearchResultList showItemTags={this.props.showItemTags} requestMoreResults={this.getMoreResults} hasMore={this.state.hasMore} results={results!} />}
                </InfiniteListLoading>
            </div>
        </Content>;
    }
}

const RecentContent = withSnackbar(withAuthContext(RecentContentPageComponent)) as React.ComponentType<IRecentContentPageProps>;

const NonExternalRecentContent = withSnackbar(withAuthContext(RecentContentPageComponent, UserLevel.External, true)) as React.ComponentType<IRecentContentPageProps>;

//export const RecentLinesPage = () => <RecentContent type={AbstractDocumentKind.MediaLines} />;

export const RecentDebatesPage = () => <RecentContent type={SearchableContentType.DebateNotes} />;

export const RecentMediaLinesPage = () => <RecentContent CustomComponent={RecentMediaLinesDocList} type={SearchableContentType.MediaLines} />

export const RecentFAQPage = () => <NonExternalRecentContent type={SearchableContentType.FAQ} />;

export const RecentBriefsPage = () => <NonExternalRecentContent type={SearchableContentType.IssueBrief} />;

export const RecentProfilesPage = () => <NonExternalRecentContent type={SearchableContentType.ProfileOfMP} />;

export const RecentPeoplePage = () => <NonExternalRecentContent type={SearchableContentType.Person} />;

export const RecentOpinionEditorialsPage = () => <NonExternalRecentContent type={SearchableContentType.OpinionEditorial} />;

export const RecentQuotesPage = () => <NonExternalRecentContent type={SearchableContentType.Quote} />;

export const RecentAchievementsPage = () => <NonExternalRecentContent type={SearchableContentType.Achievement} />;

export const RecentResourcesPage = () => <NonExternalRecentContent type={SearchableContentType.Resource} showItemTags />;



// WEBPACK FOOTER //
// ./src/pages/recent-content-page.tsx